import { Link } from "react-router-dom";
import {
  ArrowLeft, Home, Sparkles, Leaf, Star, Check, Calendar,
  Smartphone, Bot, BarChart3, Zap as ZapIcon, Droplet, Users, Cloud, LifeBuoy,
  ShieldCheck, XCircle, Lock,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";

/**
 * Home Users — Sample Plans & Billing preview.
 * A design mockup of the future in-app subscription screen for home users.
 * No live billing, no VAT line, no payment processing yet.
 */

const plans = [
  {
    id: "starter",
    name: "Starter",
    tagline: "Best for apartments & small homes",
    icon: Leaf,
    price: "AED 49",
    unit: "/ month",
    features: [
      "Up to 5 devices",
      "AI Assistant",
      "Water & Energy Analytics",
      "Basic Automation",
      "Monthly Reports",
      "Family Sharing",
      "Basic Cloud Storage",
    ],
    cta: "Current Plan",
    current: true,
  },
  {
    id: "plus",
    name: "Plus",
    tagline: "Recommended",
    icon: Home,
    price: "AED 99",
    unit: "/ month",
    features: [
      "Up to 20 devices",
      "AI Assistant (Advanced)",
      "Advanced Analytics",
      "Unlimited Automations",
      "Water Leak Detection Alerts",
      "Property Damage Insights",
      "Priority Support",
      "Extended Cloud Storage",
    ],
    cta: "Upgrade to Plus",
    accent: true,
    badge: "Most Popular",
  },
  {
    id: "pro",
    name: "Pro",
    tagline: "Best for large villas & smart home enthusiasts",
    icon: Sparkles,
    price: "AED 299",
    unit: "/ month",
    features: [
      "Unlimited devices",
      "Premium AI Assistant",
      "Advanced Analytics",
      "Multi-home Management",
      "Early Access to New Features",
      "Premium Support",
      "1 Year Full Cloud Storage",
    ],
    cta: "Upgrade to Pro",
  },
];

const compareRows = [
  { icon: Smartphone, feature: "Connected Devices", starter: "Up to 5", plus: "Up to 20", pro: "Unlimited" },
  { icon: Bot, feature: "AI Assistant", starter: "✓", plus: "Advanced", pro: "Premium" },
  { icon: BarChart3, feature: "Analytics & Reports", starter: "Basic", plus: "Advanced", pro: "Advanced+" },
  { icon: ZapIcon, feature: "Automations", starter: "Basic", plus: "Unlimited", pro: "Unlimited" },
  { icon: Droplet, feature: "Leak Detection Alerts", starter: "—", plus: "✓", pro: "✓" },
  { icon: Users, feature: "Family / Home Sharing", starter: "✓", plus: "✓", pro: "✓" },
  { icon: Cloud, feature: "Cloud Storage", starter: "Basic", plus: "Extended", pro: "1 Year Included" },
  { icon: LifeBuoy, feature: "Customer Support", starter: "Standard", plus: "Priority", pro: "Premium" },
];

const trustBadges = [
  { icon: Lock, title: "Secure Payments" },
  { icon: XCircle, title: "Cancel Anytime — No Lock-in" },
  { icon: ShieldCheck, title: "Your Data is 100% Secure" },
  { icon: Leaf, title: "Every Plan Supports a Greener Future" },
];

export default function HomeBilling() {
  return (
    <div className="bg-bone min-h-screen">
      <Navbar />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-20">
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 text-sm text-forest hover:text-forest-hover mb-6"
          data-testid="back-to-pricing"
        >
          <ArrowLeft size={16} /> Back to Pricing
        </Link>

        {/* Header */}
        <div className="flex items-center gap-3 mb-3">
          <p className="label-eyebrow">Home users</p>
          <span className="text-[10px] font-mono uppercase tracking-widest bg-white border border-[#E2DFD8] text-forest px-2 py-0.5 rounded-full">
            sample preview · not yet live
          </span>
        </div>
        <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter leading-[0.98] text-forest">
          Upgrade your plan.
        </h1>
        <p className="mt-3 text-ink/65 max-w-xl">
          Choose the perfect plan for your smart and sustainable home. This is a design preview of the in-app subscription screen — no live billing or payments yet.
        </p>

        {/* Current plan card */}
        <div className="mt-8 rounded-3xl bg-white border border-[#E2DFD8] p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6" data-testid="current-plan-home">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-full bg-forest/5 grid place-items-center text-forest">
              <Home size={22} strokeWidth={1.5} />
            </div>
            <div>
              <p className="label-eyebrow">Current plan</p>
              <p className="font-serif text-2xl mt-1 text-ink">Home Starter</p>
              <div className="flex items-center gap-3 mt-2">
                <p className="font-serif text-3xl text-forest">AED 49 <span className="text-sm text-ink/50">/ month</span></p>
                <span className="text-xs font-mono bg-moss/15 text-forest rounded-full px-3 py-1">Active</span>
              </div>
            </div>
          </div>
          <div className="md:text-right">
            <p className="label-eyebrow flex items-center gap-2 md:justify-end">
              <Calendar size={12} /> Next billing date
            </p>
            <p className="font-serif text-2xl mt-1 text-ink">15 Jul 2026</p>
            <p className="text-xs text-ink/50 mt-1">Billing cycle: Monthly</p>
          </div>
        </div>

        {/* Plans grid */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="home-plans-grid">
          {plans.map((p) => (
            <div
              key={p.id}
              className={`relative rounded-3xl p-8 border flex flex-col transition-all hover:-translate-y-1 hover:shadow-xl ${
                p.accent ? "bg-forest text-bone border-forest" : "bg-white border-[#E2DFD8]"
              }`}
              data-testid={`home-plan-${p.id}`}
            >
              {p.badge && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-terracotta text-bone text-xs font-mono px-3 py-1 rounded-full whitespace-nowrap">
                  {p.badge}
                </span>
              )}
              <div className="flex items-center gap-2">
                <div className={`w-9 h-9 rounded-full grid place-items-center ${p.accent ? "bg-bone/10 text-bone" : "bg-forest/5 text-forest"}`}>
                  <p.icon size={16} strokeWidth={1.5} />
                </div>
                <p className={`font-serif text-2xl ${p.accent ? "text-bone" : "text-ink"}`}>{p.name}</p>
                {p.accent && <Star size={14} className="text-terracotta ml-1" />}
              </div>
              <div className="mt-5 flex items-baseline gap-2 flex-wrap">
                <span className="font-serif text-5xl font-light">{p.price}</span>
                <span className={`text-sm ${p.accent ? "text-bone/60" : "text-ink/55"}`}>{p.unit}</span>
              </div>
              {p.current && (
                <span className="mt-3 inline-flex self-start text-xs font-mono bg-moss/15 text-forest rounded-full px-3 py-1">
                  Current Plan
                </span>
              )}
              <ul className="mt-6 space-y-2.5 text-sm flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check size={16} strokeWidth={1.5} className={`mt-0.5 shrink-0 ${p.accent ? "text-terracotta" : "text-forest"}`} />
                    <span className={p.accent ? "text-bone/85" : "text-ink/75"}>{f}</span>
                  </li>
                ))}
              </ul>
              <p className={`mt-6 text-xs italic ${p.accent ? "text-bone/70" : "text-ink/55"}`}>{p.tagline}</p>
              <Button
                disabled={p.current}
                className={`mt-6 w-full rounded-full h-12 ${
                  p.accent
                    ? "bg-bone text-forest hover:bg-bone/90"
                    : p.current
                    ? "bg-white border border-forest text-forest opacity-100"
                    : "bg-forest text-bone hover:bg-forest-hover"
                }`}
                data-testid={`home-plan-cta-${p.id}`}
              >
                {p.cta}
              </Button>
            </div>
          ))}
        </div>

        {/* Compare Plans */}
        <div className="mt-14 surface-card overflow-hidden" data-testid="compare-plans-table">
          <div className="p-6 md:p-8 border-b border-[#E2DFD8]">
            <p className="label-eyebrow">Compare</p>
            <h2 className="font-serif text-2xl md:text-3xl text-ink mt-1">Compare plans side-by-side</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-[#E2DFD8]">
                  <th className="py-4 px-4 md:px-8 font-medium text-ink/60 w-[38%]">Feature</th>
                  <th className="py-4 px-4 text-center font-medium text-ink">Starter</th>
                  <th className="py-4 px-4 text-center font-medium text-forest">
                    <span className="inline-flex items-center gap-1.5">Plus <Star size={12} className="text-terracotta" /></span>
                  </th>
                  <th className="py-4 px-4 text-center font-medium text-ink">Pro</th>
                </tr>
              </thead>
              <tbody>
                {compareRows.map((r) => (
                  <tr key={r.feature} className="border-b border-[#E2DFD8] last:border-b-0 hover:bg-bonewarm/40">
                    <td className="py-4 px-4 md:px-8">
                      <div className="flex items-center gap-3">
                        <r.icon size={16} strokeWidth={1.5} className="text-forest shrink-0" />
                        <span className="text-ink">{r.feature}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-center text-ink/70">{r.starter}</td>
                    <td className="py-4 px-4 text-center text-forest font-medium">{r.plus}</td>
                    <td className="py-4 px-4 text-center text-ink/70">{r.pro}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Message banner */}
        <div className="mt-10 rounded-3xl bg-moss/15 border border-moss/30 p-6 md:p-8 flex items-start gap-4" data-testid="upgrade-banner">
          <div className="w-10 h-10 rounded-full bg-moss text-bone grid place-items-center shrink-0">
            <Leaf size={18} strokeWidth={1.5} />
          </div>
          <div>
            <p className="font-serif text-xl md:text-2xl text-forest">More savings. More protection. A more sustainable home.</p>
            <p className="mt-2 text-sm text-ink/65">
              Upgrade anytime. You can change or cancel your plan whenever you want.
            </p>
          </div>
        </div>

        {/* Trust badges */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="trust-badges">
          {trustBadges.map((b) => (
            <div key={b.title} className="bg-white rounded-2xl border border-[#E2DFD8] p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-forest/5 text-forest grid place-items-center shrink-0">
                <b.icon size={16} strokeWidth={1.5} />
              </div>
              <p className="text-xs text-ink/70 leading-snug">{b.title}</p>
            </div>
          ))}
        </div>

        <p className="mt-10 text-xs text-ink/50 leading-relaxed max-w-3xl" data-testid="home-billing-disclaimer">
          This page is a design preview of the in-app subscription screen for home users. Plans and current-plan indicators are illustrative only — no live subscriptions or payments are processed yet. Final pricing, taxes and regional adjustments will be published before general availability.
        </p>
      </main>

      <Footer />
    </div>
  );
}
